import React from "react";
import { withStyles } from "@material-ui/styles";
import { I18n } from "@aws-amplify/core";
import update from "immutability-helper";
import { get, includes, map, filter, isEmpty, toString } from "lodash";
import clsx from "clsx";

import { fetchPut } from "../../../api/fetch";

import Grid from "@material-ui/core/Grid";
import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";

import DetailsContainer from "../../Common/DetailsContainer";
import CustomButton from "../../Common/CustomButton";
import InfoSection from "../../Common/InfoSection";
import FormSection from "../../Common/FormSection";
import Timeline from "../Timeline";
import MatchSuccess from "../MatchSuccess";

import { formatDate } from "../../../utils/date";
import { getLov } from "../../../utils/lov";
import { getAttributeValue, getLovValue } from "../../../utils/profile";
import { getAttributeName } from "../../../utils/attributes";

import { driveInOut, walkInOut } from "../../../constants/reducers";

import images from "../../../assets/img/images";
import { isAllowed } from "../../../utils/users";

const styles = (theme) => ({
  timelineCol: {
    [theme.breakpoints.up("lg")]: {
      marginRight: "50px",
    },
    [theme.breakpoints.down("xl")]: {
      marginBottom: "20px",
    },
  },
});

const LogDetailsSummaryNew = (props) => {
  const {
    classes,
    log,
    language,
    refreshLog,
    section,
    permissions,
    dbPermissions,
  } = props;
  const [lov, setLov] = React.useState({});
  const [edition, setEdition] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const blockEdition = !isEmpty(dbPermissions)
    ? !isAllowed(dbPermissions, "events.update")
    : get(permissions, ["update"]) === false;

  React.useEffect(() => {
    fetchLov();
  }, []);

  const fetchLov = async () => {
    setLov({
      riskLevel: await getLov("riskLevel"),
      status: await getLov("status"),
      105: await getLov("105"),
    });
  };

  const handleChange = (id, index, value) => {
    setEdition(
      update(edition, {
        [id]: { $set: value },
      })
    );
  };

  const submit = async () => {
    try {
      setSaving(true);
      let body = {};
      if (edition.status) body.status = parseInt(edition.status);
      if (edition.riskLevel) body.risk_level = parseInt(edition.riskLevel);
      if (edition.comments !== undefined) body.description = edition.comments;
      if (edition.active !== undefined) body.active = edition.active;
      await fetchPut(`event_groups/${log.id}`, body);
      refreshLog();
      setEdition(false);
      setSaving(false);
    } catch (e) {
      console.log(e);
    }
  };

  const mainEvent = get(
    filter(
      get(log, ["events"]),
      (e) =>
        includes([58, 59], get(e, ["type", "id"])) ||
        (includes([60, 61], get(e, ["type", "id"])) &&
          get(e, ["profile", "type", "id"]) === 2)
    ),
    0
  );

  return (
    <Grid container>
      <Grid
        item
        xs={3}
        style={{
          padding: "0px 24px 24px 24px",
          marginRight: "20px",
          maxWidth: "300px",
        }}
      >
        <Grid container direction="column">
          <Grid item>
            <Grid container>
              {edition ? (
                <Grid
                  item
                  className="bold-500"
                  style={{
                    padding: "0px 8px 8px 0px",
                    margin: "-2px 0px 0px -4px",
                  }}
                >
                  <Checkbox
                    color="primary"
                    checked={
                      edition.active !== undefined
                        ? edition.active
                        : get(log, ["active"])
                    }
                    onChange={(e) =>
                      handleChange("active", null, e.target.checked)
                    }
                  />
                  {I18n.get("profiles_active")}
                </Grid>
              ) : (
                <InfoSection title="active" values={[get(log, ["active"])]} />
              )}
              <Grid
                item
                xs
                className="flexCenter"
                style={{ justifyContent: "flex-end", paddingBottom: "8px" }}
              >
                {edition ? (
                  saving ? (
                    <CircularProgress size={20} />
                  ) : (
                    <>
                      <CustomButton
                        color="primary"
                        style={{ margin: "0px 10px 0px 0px" }}
                        onClick={() => setEdition(false)}
                      >
                        {I18n.get("site_cancel")}
                      </CustomButton>
                      <CustomButton
                        variant="contained"
                        color="primary"
                        onClick={submit}
                      >
                        {I18n.get("site_save")}
                      </CustomButton>
                    </>
                  )
                ) : (
                  <img
                    src={
                      blockEdition
                        ? images.editIconColored
                        : images.editIconDisabled
                    }
                    alt="edit"
                    style={{ cursor: blockEdition ? "pointer" : "" }}
                    onClick={() => {fetchLov(); setEdition({});}}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
          {edition ? (
            <FormSection
              style={{ padding: "8px", marginBottom: "0px" }}
              formValue={[
                edition.riskLevel !== undefined
                  ? edition.riskLevel
                  : toString(get(log, ["risk_level"])),
              ]}
              setFormValue={handleChange}
              title="riskLevel"
              id="riskLevel"
              type="select"
              options={{
                options: map(lov.riskLevel, (t) => ({
                  id: toString(get(t, ["id"])),
                  name: t[`name_${language}`],
                })),
              }}
            />
          ) : (
            <InfoSection
              title="riskLevel"
              values={[
                getLovValue(
                  "riskLevel",
                  get(log, ["risk_level"]),
                  lov,
                  language
                ),
              ]}
            />
          )}
          <InfoSection
            title="dateTime"
            values={[
              formatDate(
                getAttributeValue(mainEvent, 76) ||
                  getAttributeValue(mainEvent, 77)
              ),
            ]}
          />
          {edition ? (
            <FormSection
              style={{ padding: "8px", marginBottom: "0px" }}
              formValue={[
                edition.status !== undefined
                  ? edition.status
                  : toString(get(log, ["status", "id"])),
              ]}
              setFormValue={handleChange}
              title="status"
              id="status"
              type="select"
              options={{
                options: map(lov.status, (t) => ({
                  id: toString(get(t, ["id"])),
                  name: t[`name_${language}`],
                })),
              }}
            />
          ) : (
            <InfoSection
              title="status"
              values={[get(log, ["status", `name_${language}`])]}
            />
          )}
          {edition ? (
            <FormSection
              style={{ padding: "8px", marginBottom: "0px" }}
              formValue={[
                edition.comments !== undefined
                  ? edition.comments
                  : get(log, ["description"]),
              ]}
              setFormValue={handleChange}
              title="comments"
              id="comments"
              options={{ multiline: true, height: "250px" }}
            />
          ) : (
            <InfoSection
              title="comments"
              values={[get(log, ["description"])]}
            />
          )}
          <InfoSection
            title="logType"
            values={[get(log, ["type", `name_${language}`])]}
          />
          <InfoSection
            title2={getAttributeName(105)}
            values={[getAttributeValue(mainEvent, 105, lov, language)]}
          />
          <InfoSection
            title="modifiedOn"
            values={[formatDate(get(log, ["created_on"]))]}
          />
          <InfoSection
            title="processedBy"
            values={[get(log, ["created_by"])]}
          />
        </Grid>
      </Grid>
      <Grid
        item
        className={clsx({ "MuiGrid-grid-xs-true": section === walkInOut })}
      >
        <Grid container>
          <Grid
            item
            xs={12}
            xl={6}
            style={{
              maxWidth: props.section === driveInOut ? "800px" : "550px",
              minWidth: "550px",
            }}
            className={classes.timelineCol}
          >
            <Timeline
              section={section}
              data={get(log, ["timeline"])}
              eventGroupId={get(log, ["id"])}
              log={log}
              blockLevel2={props.blockLevel2}
            />
          </Grid>
        </Grid>
      </Grid>
      {section === driveInOut && (
        <Grid
          item
          style={{ padding: "0px 10px", minWidth: "330px", maxWidth: "330px" }}
        >
          <DetailsContainer
            title={I18n.get("site_matchSuccess")}
            style={{ paddingBottom: 0, paddingLeft: "5px" }}
          >
            <MatchSuccess data={get(log, ["timeline"])} />
          </DetailsContainer>
        </Grid>
      )}
    </Grid>
  );
};

export default withStyles(styles)(LogDetailsSummaryNew);
